import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchWithAuthRetry, handleApiResponse } from './apiUtils';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export type TagSort = 'name' | 'usage';
export type TagSortOrder = 'asc' | 'desc';

export interface TagSummary {
  id: number;
  name: string;
}

export interface TagUsage extends TagSummary {
  courseCount: number;
  lessonCount: number;
  totalCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface TagListResponse {
  items: TagUsage[];
  totalCount: number;
}

export interface ItemTagsResponse {
  items: TagSummary[];
}

export interface CreateTagResult {
  tag: TagSummary;
  nearMatches: TagSummary[];
}

export interface RenameTagResult {
  tag: TagSummary;
  courseCount: number;
  lessonCount: number;
}

export interface DeleteTagResult {
  courseCount: number;
  lessonCount: number;
}

/** A tag name that already exists (case-insensitive) — carries the existing tag's id so the UI can point to it. */
export class TagConflictError extends Error {
  constructor(message: string, public readonly existingTagId: number) {
    super(message);
  }
}

async function parseTagError(response: Response, fallback: string): Promise<Error> {
  const body = await response.json().catch(() => null) as { message?: string; existingTagId?: number } | null;
  const message = body?.message || fallback;
  if (response.status === 409 && typeof body?.existingTagId === 'number') {
    return new TagConflictError(message, body.existingTagId);
  }
  return new Error(message);
}

export const tagKeys = {
  all: ['tags'] as const,
  lists: () => [...tagKeys.all, 'list'] as const,
  // assignedAccountId is part of the key so a client-account-scoped count and
  // the global count never collide in the cache — switching Content Context
  // must show the right number immediately, not a stale one from the other scope.
  list: (search: string, sort: TagSort, order: TagSortOrder, assignedAccountId: string) =>
    [...tagKeys.lists(), search, sort, order, assignedAccountId] as const,
  courseTags: (courseId: string) => [...tagKeys.all, 'course', courseId] as const,
  lessonTags: (lessonId: string) => [...tagKeys.all, 'lesson', lessonId] as const,
};

export async function listTags(options: {
  search?: string;
  sort?: TagSort;
  order?: TagSortOrder;
  /** Scopes courseCount/lessonCount to this account's assigned items. Omit for the global count. */
  assignedAccountId?: string;
  signal?: AbortSignal;
} = {}): Promise<TagListResponse> {
  const params = new URLSearchParams();
  if (options.search?.trim()) params.set('q', options.search.trim());
  if (options.sort) params.set('sort', options.sort);
  if (options.order) params.set('order', options.order);
  if (options.assignedAccountId && /^\d+$/.test(options.assignedAccountId)) {
    params.set('assignedAccountId', options.assignedAccountId);
  }
  const query = params.toString();
  const response = await fetchWithAuthRetry(
    `${API_BASE_URL}/tags${query ? `?${query}` : ''}`,
    { signal: options.signal },
    { json: 'withBody' },
  );
  return handleApiResponse<TagListResponse>(response, 'Failed to load tags');
}

export async function createTag(name: string): Promise<CreateTagResult> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/tags`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  }, { json: 'withBody' });
  if (!response.ok) throw await parseTagError(response, 'Failed to create tag');
  return response.json();
}

export async function renameTag(id: number, name: string): Promise<RenameTagResult> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/tags/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ name }),
  }, { json: 'withBody' });
  if (!response.ok) throw await parseTagError(response, 'Failed to rename tag');
  return response.json();
}

export async function deleteTag(id: number): Promise<DeleteTagResult> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/tags/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw await parseTagError(response, 'Failed to delete tag');
  return response.json();
}

export async function getCourseTags(courseId: string, signal?: AbortSignal): Promise<ItemTagsResponse> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/courses/${courseId}/tags`, { signal }, { json: 'withBody' });
  return handleApiResponse<ItemTagsResponse>(response, 'Failed to load tags for this course');
}

export async function getLessonTags(lessonId: string, signal?: AbortSignal): Promise<ItemTagsResponse> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/lessons/${lessonId}/tags`, { signal }, { json: 'withBody' });
  return handleApiResponse<ItemTagsResponse>(response, 'Failed to load tags for this lesson');
}

export async function assignCourseTag(courseId: string, tagId: number): Promise<ItemTagsResponse> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/courses/${courseId}/tags`, {
    method: 'POST',
    body: JSON.stringify({ tagId }),
  }, { json: 'withBody' });
  return handleApiResponse<ItemTagsResponse>(response, 'Failed to add tag');
}

export async function unassignCourseTag(courseId: string, tagId: number): Promise<ItemTagsResponse> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/courses/${courseId}/tags/${tagId}`, {
    method: 'DELETE',
  }, { json: 'withBody' });
  return handleApiResponse<ItemTagsResponse>(response, 'Failed to remove tag');
}

export async function assignLessonTag(lessonId: string, tagId: number): Promise<ItemTagsResponse> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/lessons/${lessonId}/tags`, {
    method: 'POST',
    body: JSON.stringify({ tagId }),
  }, { json: 'withBody' });
  return handleApiResponse<ItemTagsResponse>(response, 'Failed to add tag');
}

export async function unassignLessonTag(lessonId: string, tagId: number): Promise<ItemTagsResponse> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/lessons/${lessonId}/tags/${tagId}`, {
    method: 'DELETE',
  }, { json: 'withBody' });
  return handleApiResponse<ItemTagsResponse>(response, 'Failed to remove tag');
}

/**
 * The full tag library, with usage counts — backs both the Tags screen and
 * every picker's "ALL TAGS" browse dropdown. `refetchOnWindowFocus` is
 * overridden to true here (the app disables it globally in queryClient.ts):
 * a rename/usage change made on the Tags screen in one tab should reach an
 * already-open picker in another without a manual reload, and this is the
 * one shared list every surface reads from.
 */
export function useTagsQuery(options: {
  search?: string;
  sort?: TagSort;
  order?: TagSortOrder;
  /** Scopes courseCount/lessonCount to this account's assigned items (the current Content Context). Omit/undefined for Standard's global count. */
  assignedAccountId?: string;
  enabled?: boolean;
} = {}) {
  const search = options.search?.trim() ?? '';
  const sort = options.sort ?? 'name';
  const order = options.order ?? 'asc';
  const assignedAccountId = options.assignedAccountId ?? '';
  return useQuery({
    queryKey: tagKeys.list(search, sort, order, assignedAccountId),
    queryFn: ({ signal }) => listTags({ search, sort, order, assignedAccountId, signal }),
    enabled: options.enabled ?? true,
    refetchOnWindowFocus: true,
  });
}

export function useCreateTagMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createTag(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagKeys.lists() });
    },
  });
}

// Renaming/deleting a tag can change what's shown anywhere its name or
// membership is cached (item-scoped picker chips included), not just the
// library list — invalidate the whole tags namespace rather than trying to
// enumerate every affected courseId/lessonId cache entry.
export function useRenameTagMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => renameTag(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagKeys.all });
    },
  });
}

export function useDeleteTagMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteTag(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagKeys.all });
    },
  });
}

export function useCourseTagsQuery(courseId: string | null) {
  return useQuery({
    queryKey: tagKeys.courseTags(courseId ?? ''),
    queryFn: ({ signal }) => getCourseTags(courseId as string, signal),
    enabled: Boolean(courseId),
  });
}

export function useLessonTagsQuery(lessonId: string | null) {
  return useQuery({
    queryKey: tagKeys.lessonTags(lessonId ?? ''),
    queryFn: ({ signal }) => getLessonTags(lessonId as string, signal),
    enabled: Boolean(lessonId),
  });
}

// Usage counts on the shared list (tagKeys.lists()) shift on every
// assign/unassign, so mutations invalidate both the item-scoped cache entry
// (replaced directly with the response — no refetch needed) and the lists.
export function useAssignCourseTagMutation(courseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tagId: number) => assignCourseTag(courseId, tagId),
    onSuccess: (data) => {
      queryClient.setQueryData(tagKeys.courseTags(courseId), data);
      queryClient.invalidateQueries({ queryKey: tagKeys.lists() });
    },
  });
}

export function useUnassignCourseTagMutation(courseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tagId: number) => unassignCourseTag(courseId, tagId),
    onSuccess: (data) => {
      queryClient.setQueryData(tagKeys.courseTags(courseId), data);
      queryClient.invalidateQueries({ queryKey: tagKeys.lists() });
    },
  });
}

export function useAssignLessonTagMutation(lessonId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tagId: number) => assignLessonTag(lessonId, tagId),
    onSuccess: (data) => {
      queryClient.setQueryData(tagKeys.lessonTags(lessonId), data);
      queryClient.invalidateQueries({ queryKey: tagKeys.lists() });
    },
  });
}

export function useUnassignLessonTagMutation(lessonId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tagId: number) => unassignLessonTag(lessonId, tagId),
    onSuccess: (data) => {
      queryClient.setQueryData(tagKeys.lessonTags(lessonId), data);
      queryClient.invalidateQueries({ queryKey: tagKeys.lists() });
    },
  });
}
